import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import api from '../../utils/api';
import { usePublicData } from '../../context/PublicDataContext';
import { img, labelize, Eyebrow, Display, Reveal, Skeleton } from './components/ui';
import Seo from './components/Seo';

/* Projects = a curated gallery of completed work. Sourced from the existing
 * website_media library (GET /website/public/media), grouped by its category
 * field (hotels / homes / restaurants / commercial …). No new backend. */
export default function ProjectsPage() {
  const { sections, siteName } = usePublicData();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/website/public/media', { params: { category: 'projects' } });
        let rows = Array.isArray(res.data) ? res.data : [];
        // Fall back to the whole library if nothing is tagged "projects".
        if (!rows.length) {
          const all = await api.get('/website/public/media');
          rows = Array.isArray(all.data) ? all.data : [];
        }
        if (alive) setMedia(rows);
      } catch {
        if (alive) setMedia([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const cats = useMemo(() => {
    const s = new Set(media.map((m) => m.category || 'general'));
    return ['all', ...[...s]];
  }, [media]);

  const shown = active === 'all' ? media : media.filter((m) => (m.category || 'general') === active);
  const intro = sections.projects || {};

  return (
    <>
      <Seo title="Projects" description={`Completed furniture projects by ${siteName} — hospitality, residential and commercial spaces.`} siteName={siteName} />

      <section className="pt-36 pb-14 bg-[#241811] text-[#f4ece0]">
        <div className="max-w-6xl mx-auto px-6">
          <Eyebrow tone="light">Selected work</Eyebrow>
          <Display as="h1" className="text-4xl md:text-[56px] mt-4 max-w-3xl">
            {intro.title || 'Projects delivered across homes, hotels and workspaces.'}
          </Display>
          {(intro.body || intro.subtitle) && (
            <p className="text-[#cbbca4] max-w-xl mt-6 text-[15.5px] leading-relaxed">{intro.body || intro.subtitle}</p>
          )}
        </div>
      </section>

      {cats.length > 2 && (
        <div className="border-b border-[#241811]/10 sticky top-[var(--public-header-h,104px)] z-30 bg-[#f4ece0f2] backdrop-blur">
          <div className="max-w-6xl mx-auto px-6 py-3 flex gap-3 overflow-x-auto">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`whitespace-nowrap text-[12px] uppercase tracking-[0.12em] px-4 py-2 border transition-colors ${active === c ? 'bg-[#241811] text-[#f4ece0] border-[#241811]' : 'border-[#241811]/20 hover:border-[#241811]'}`}
              >
                {c === 'all' ? 'All' : labelize(c)}
              </button>
            ))}
          </div>
        </div>
      )}

      <section className="py-16 min-h-[40vh]">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-64 mb-5" />)}
            </div>
          ) : shown.length ? (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
              {shown.map((m, i) => (
                <Reveal key={m.id} delay={(i % 3) * 80} className="mb-5 break-inside-avoid">
                  <button onClick={() => setLightbox(m)} className="group block w-full overflow-hidden bg-[#ece0cd]" aria-label={m.alt_text || 'Project image'}>
                    <img src={img(m.url)} alt={m.alt_text || ''} loading="lazy" className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </button>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-[#4a3d32]">
              <p className="font-display text-2xl mb-2 text-[#241811]">Project gallery coming soon</p>
              <p className="text-sm">Upload images to the “projects” media category in the admin panel and they'll appear here.</p>
            </div>
          )}
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[85] bg-[#160f0ae6] flex items-center justify-center p-4" onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <button className="absolute top-5 right-5 text-white/80 hover:text-white" aria-label="Close"><X /></button>
          <img src={img(lightbox.url)} alt={lightbox.alt_text || ''} className="max-w-full max-h-[88vh] object-contain" />
        </div>
      )}
    </>
  );
}
